import { useEffect, useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Box, Button, makeStyles, Checkbox } from "@material-ui/core";
import DetailsDialog, { DetailsProps } from "../DetailsDialog";
import { areDimensionsEqual, Dimension, Identifiable, Price } from "../../model/Items";
import { CUBIC_METERS_IN_UNIT, CUBIC_METER_IN_CENTIMETERS } from "../../engine/Calculations";
import TextInputField from "../ui/TextInputField";
import NumberInputField from "../ui/NumberInputField";
import { incrementByPercent } from "../../engine/PriceUtilities";
import usePriceAdjustmentSuggestions from "../PriceAdjustmentSuggestions";
import { fetchShapePriceAdjustmentSuggestions } from "../../api/PriceAdjustmentSuggestionApi";
import { Material } from "../../api/MaterialApi";
import useShapeApi from "../../api/ShapeApi";
import useMaterialPricingApi from "../../api/MaterialPricingApi.hook";

interface Props extends DetailsProps {
  readonly customerId: number;
  readonly priceListId: number;
}

interface Shape {
  id?: number;
  name: string;
  block: Dimension;
  material: Identifiable;
  unitPrice: Price;
  unitQuantity: number;
  unitWeight: number;
}

const enum CuttingMode {
  Standard = 'standard',
  TwoAxis = 'two-axis'
}

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  inputField: {
    minWidth: 280
  }
}));

const ShapeDetails = (props: Props) => {
  const classes = useStyles();
  const shapeApi = useShapeApi();
  const materialPricingApi = useMaterialPricingApi();
  const priceAdjustmentSuggestions = usePriceAdjustmentSuggestions(fetchShapePriceAdjustmentSuggestions);
  const [materials, setMaterials] = useState<Material[]>();
  const [suggestedPriceAdjustment, setSuggestedPriceAdjustment] = useState<number>();
  const [hideNonPricingMaterials, setHideNonPricingMaterials] = useState<boolean>(true);
  const [userSelectedMaterial, setUserSelectedMaterial] = useState<Material|null>(null);
  const [initialMaterialId, setInitialMaterialId] = useState<number>();
  const [name, setName] = useState<string>();
  const [blockLength, setBlockLength] = useState<number>();
  const [blockWidth, setBlockWidth] = useState<number>();
  const [blockDepth, setBlockDepth] = useState<number>();
  const [unitBasePrice, setUnitBasePrice] = useState<number>();
  const [priceAdjustment, setPriceAdjustment] = useState<number>();
  const [unitEffectivePrice, setUnitEffectivePrice] = useState<number|null>();
  const [unitQuantity, setUnitQuantity] = useState<number>();
  const [unitWeight, setUnitWeight] = useState<number>();
  const [forceSaveOnValidationFailedMessage, setForceSaveOnValidationFailedMessage] = useState<string>();
  const [volume, setVolume] = useState<number|null>();
  const [cuttingMode, setCuttingMode] = useState<string|null>();
  const isTwoAxisCuttingMode = cuttingMode === CuttingMode.TwoAxis;
  const customerId = props.customerId;
  const priceListId = props.priceListId;

  const initializeFields = (data: any) => {
    const block = data.block;
    const unitPrice = data.unitPrice;
    setUserSelectedMaterial(null);
    setInitialMaterialId(data.material.id);
    setName(data.name);
    setBlockLength(block.length);
    setBlockWidth(block.width);
    setBlockDepth(block.depth);
    setUnitBasePrice(unitPrice.base);
    setPriceAdjustment(unitPrice.adjustment);
    setUnitEffectivePrice(unitPrice.effective);
    setUnitQuantity(data.unitQuantity);
    setUnitWeight(data.unitWeight);
    setCuttingMode(data.cuttingMode);
  };

  const collectData = () => 
    (name
      && userSelectedMaterial
      && blockLength && blockWidth && blockDepth
      && unitBasePrice
      && unitQuantity
      && unitWeight)
     ? {
      name,
      material: { id: userSelectedMaterial.id },
      block: { length: blockLength, width: blockWidth, depth: blockDepth },
      unitPrice: { base: unitBasePrice, adjustment: priceAdjustment, effective: null },
      unitQuantity,
      unitWeight,
      cuttingMode
    }
    : null;

  const fetchMaterials = () => {
    materialPricingApi
      .getCustomerMaterialsWithPricingsApplied(customerId, priceListId, hideNonPricingMaterials)
      .then(materials => materials.sort((material, anotherMaterial) => material.name.localeCompare(anotherMaterial.name)))
      .then(materials => setMaterials(materials))
      .catch(reason => console.error(`Could not fetch materials: ${reason}`));
  };

  const calculateBlockVolume = (length?: number, width?: number, depth?: number) =>
    length && width && depth ? (length * width * depth / CUBIC_METER_IN_CENTIMETERS) : 0;

  const calculateAndSetEffectivePrice = (basePrice: number) => {
    const effectivePrice = priceAdjustment
      ? incrementByPercent(basePrice, priceAdjustment)
      : basePrice;
    setUnitEffectivePrice(effectivePrice);
  };

  const calculateAndSetUnitPrices = (unitPrice: number, blockVolume: number) => {
    if (unitQuantity) {
      const shapeUnitBasePrice = unitPrice * blockVolume / CUBIC_METERS_IN_UNIT / unitQuantity;
      console.debug(`shapeUnitBasePrice: ${shapeUnitBasePrice}: ${unitQuantity}`);
      if (shapeUnitBasePrice) {
        const roundedShapeUnitBasePrice = Number(shapeUnitBasePrice.toFixed(2));
        setUnitBasePrice(roundedShapeUnitBasePrice);
        calculateAndSetEffectivePrice(roundedShapeUnitBasePrice);
      }
    }
  };

  const calculateUnitWeight = (density: number, blockVolume: number, unitQuantity?: number) => {
    if (unitQuantity) {
      const shapeUnitWeight = density * blockVolume / Number(unitQuantity);
      if (shapeUnitWeight) {
        setUnitWeight(Number(shapeUnitWeight.toFixed(3)));
      }
    }
  };

  const handleMaterialChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedMaterial = event.target.value as Material;
    setUserSelectedMaterial(selectedMaterial);
    const blockVolume = calculateBlockVolume(blockLength, blockWidth, blockDepth);
    setVolume(blockVolume);
    calculateUnitWeight(selectedMaterial.density, blockVolume, unitQuantity);
  };

  const handleBlockLengthChange = (length: number) => {
    setBlockLength(length);
    if (userSelectedMaterial !== null && unitQuantity !== undefined) {
      const blockVolume = calculateBlockVolume(length, blockWidth, blockDepth);
      setVolume(blockVolume);
      calculateUnitWeight(userSelectedMaterial.density, blockVolume, unitQuantity);
    }
  };

  const handleBlockWidthChange = (width: number) => {
    setBlockWidth(width);
    if (userSelectedMaterial !== null && unitQuantity !== undefined) {
      const blockVolume = calculateBlockVolume(blockLength, width, blockDepth);
      setVolume(blockVolume);
      calculateUnitWeight(userSelectedMaterial.density, blockVolume, unitQuantity);
    }
  };

  const handleBlockDepthChange = (depth: number) => {
    setBlockDepth(depth);
    if (userSelectedMaterial !== null && unitQuantity !== undefined) {
      const blockVolume = calculateBlockVolume(blockLength, blockWidth, depth);
      setVolume(blockVolume);
      calculateUnitWeight(userSelectedMaterial.density, blockVolume, unitQuantity);
    }
  };

  const handleUnitQuantityChange = (unitQuantity: number) => {
    setUnitQuantity(unitQuantity);
    if (userSelectedMaterial !== null) {
      const blockVolume = calculateBlockVolume(blockLength, blockWidth, blockDepth);
      setVolume(blockVolume);
      calculateUnitWeight(userSelectedMaterial.density, blockVolume, unitQuantity);
    }
  };

  const reportAlert = (message: string) => props.reportAlert && props.reportAlert(message);

  const validateShape = async (shape: Shape) => {
    const shapes = await shapeApi.fetchShapes(customerId, priceListId);
    const shapeWithSameDimension = shapes
      .filter(fetchedShape => props.instanceId !== fetchedShape.id)
      .find(fetchedShape => shape.material.id === fetchedShape.material.id && areDimensionsEqual(shape.block, fetchedShape.block));
    const duplicateFound = shapeWithSameDimension !== undefined;
    const message = duplicateFound
      ? `Znaleziono wykrój z tego samego materiału z identycznymi wymiarami: ${shapeWithSameDimension?.name}`
      : undefined;
    setForceSaveOnValidationFailedMessage(message);
    return !duplicateFound;
  };

  const afterShapeSave = (responseData: object, closeDialog: boolean) => {
    props.afterSave(responseData, closeDialog);
    const responseCalculatedEffectivePrice = (responseData as Shape).unitPrice.effective;
    console.debug(`### onShapeSave: ${unitEffectivePrice} : ${responseCalculatedEffectivePrice}`);
    if (unitEffectivePrice !== responseCalculatedEffectivePrice) {
      reportAlert(`
        Usługa zmodyfikowała zaprezentowaną wcześniej cenę dla klienta
        z ${unitEffectivePrice} na ${responseCalculatedEffectivePrice}`);
    }
  };

  const handleHideNonPricingMaterialsChecked
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setHideNonPricingMaterials(checked);

  const handleCuttingModeChangedToTwoAxis
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setCuttingMode(checked ? CuttingMode.TwoAxis : CuttingMode.Standard);

  const initializeSuggestedPriceAdjustment = (volume: number) => {
    const suggestedPriceAdjustment = priceAdjustmentSuggestions.findPriceAdjutmentSuggestionForVolume(volume);
    const suggestedPriceAdjustmentIncludingCuttingMode = includeCuttingModeCostInSuggestedPriceAdjustment(suggestedPriceAdjustment);
    setSuggestedPriceAdjustment(suggestedPriceAdjustmentIncludingCuttingMode);
  };

  const includeCuttingModeCostInSuggestedPriceAdjustment = (suggestedPriceAdjustment?: number) =>
    isTwoAxisCuttingMode
      ? (suggestedPriceAdjustment === undefined ? 0 : suggestedPriceAdjustment) + 10
      : suggestedPriceAdjustment;

  const handleApplySuggestedPriceAdjustment = () => setPriceAdjustment(suggestedPriceAdjustment);

  useEffect(() => {
    if (volume !== undefined && volume !== null) {
      initializeSuggestedPriceAdjustment(volume)
    }
  }, [priceAdjustmentSuggestions, cuttingMode]);

  useEffect(fetchMaterials, [hideNonPricingMaterials]);

  useEffect(() => {
    if (userSelectedMaterial) {
      const blockVolume = calculateBlockVolume(blockLength, blockWidth, blockDepth);
      setVolume(blockVolume);
      calculateAndSetUnitPrices(userSelectedMaterial.unitPrice, blockVolume);
      initializeSuggestedPriceAdjustment(blockVolume);
    }
  }, [userSelectedMaterial, blockLength, blockWidth, blockDepth, unitQuantity]);

  useEffect(() => {
    if (unitBasePrice === null || unitBasePrice === undefined) {
      setUnitEffectivePrice(null);
    } else {
      calculateAndSetEffectivePrice(unitBasePrice);
    }
  });

  useEffect(() => {
    if (materials && initialMaterialId) {
      const selectedMaterial = materials.find(item => item.id === initialMaterialId);
      setUserSelectedMaterial(selectedMaterial || null);
    }
  }, [materials, initialMaterialId]);

  const renderFields = (initialData: Shape|undefined) => (
    <>
      <h2>Wprowadź dane wykroju</h2>
      <FormControlLabel
        label="Ukryj materiały spoza cennika"
        control={
          <Switch
            color='primary'
            defaultChecked
            checked={hideNonPricingMaterials}
            onChange={handleHideNonPricingMaterialsChecked}
            size='small'
          />}
      />
      {materials && <FormControl>
        <InputLabel>Materiał</InputLabel>
        <Select value={userSelectedMaterial || ''} onChange={handleMaterialChange}>
          {materials.map(material => (<MenuItem key={material.id} value={material as any}>{material.name}</MenuItem>))}
        </Select>
      </FormControl>}
      <TextInputField
         label='Nazwa'
         autoFocus
         initialValue={initialData?.name}
         onValueChange={setName} />
      <NumberInputField
        label='Długość (bryły bazowej)'
        initialValue={initialData?.block.length}
        onValueChange={handleBlockLengthChange} />
      <NumberInputField
         label='Szerokość (bryły bazowej)'
         initialValue={initialData?.block.width}
         onValueChange={handleBlockWidthChange} />
      <NumberInputField
         label='Wysokość (bryły bazowej)'
         initialValue={initialData?.block.depth}
         onValueChange={handleBlockDepthChange} />
      <NumberInputField
        label='Objętość [m3] (bryły bazowej)'
        initialValue={volume === null ? undefined : volume}
        disabled
      />
      <NumberInputField
        label='Cena bazowa (jednostkowa)'
        initialValue={initialData?.unitPrice.base}
        calculatedValue={unitBasePrice}
        disabled />
      <NumberInputField
        label='Dodatek do ceny (jednostkowej) [%]'
        initialValue={ initialData?.unitPrice.adjustment }
        calculatedValue={ priceAdjustment }
        onValueChange={ setPriceAdjustment } />
      { suggestedPriceAdjustment !== priceAdjustment && <Box display='flex' flexDirection='row'>
        <NumberInputField
          classes={ classes }
          label='Sugerowany dodatek do ceny [%]'
          initialValue={ suggestedPriceAdjustment }
          calculatedValue={ suggestedPriceAdjustment }
          disabled
        />
        <Button
          color='primary'
          className={ classes.button }
          onClick={ handleApplySuggestedPriceAdjustment }
          variant='contained'>
            Przepisz sugerowany dodatek
        </Button>
      </Box> }
      <NumberInputField
        label='Cena (jednostkowa) dla klienta'
        initialValue={initialData?.unitPrice.effective || undefined}
        calculatedValue={unitEffectivePrice}
        disabled
      />
      <NumberInputField
        label='Ilość'
        initialValue={initialData?.unitQuantity}
        onValueChange={handleUnitQuantityChange} />
      <NumberInputField
        label='Waga (jednostkowa)'
        initialValue={initialData?.unitWeight}
        calculatedValue={unitWeight}
        onValueChange={setUnitWeight} />
      <FormControlLabel
        label='Cięcie w dwóch osiach'
        control={<Checkbox
          checked={ isTwoAxisCuttingMode }
          onChange={ handleCuttingModeChangedToTwoAxis }
        />}
      />
    </>
  );

  return (
    <DetailsDialog
      { ...props }
      resource={`customers/${customerId}/price-lists/${priceListId}/shapes`}
      renderFields={renderFields}
      initializeFields={initializeFields}
      collectData={collectData}
      afterSave={afterShapeSave}
      validateData={validateShape}
      forceSaveOnValidationFailedMessage={forceSaveOnValidationFailedMessage}
    />
  );
};
export default ShapeDetails;
